/**
 * Story 052 — Forgot-password modal on /login (AC18).
 *
 * No-auth POST to /api/auth/v1/password-reset/request. Endpoint always
 * returns 204 — anti-enumeration (OWASP ASVS V2.1.4). The UI always
 * surfaces the SAME info message regardless of whether the email was
 * known.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { getAuthBaseUrl } from "./config.js";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ForgotPasswordModal({ open, onClose }: Props): ReactElement {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await fetch(`${getAuthBaseUrl()}/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch {
      // Endpoint is anti-enumeration — we ALWAYS surface the same
      // message regardless of error. The reset link itself is mailed
      // out-of-band; FE has no further obligation here.
    }
    setSubmitting(false);
    setSubmitted(true);
  }

  function handleClose() {
    setEmail("");
    setSubmitted(false);
    setSubmitting(false);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="forgot-password-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="forgot-password-dialog-title">
        Reset password
      </DialogTitle>
      <DialogContent>
        {submitted ? (
          <Alert severity="info" role="status">
            If that email exists in our system, we sent a password-reset link.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              fullWidth
              autoComplete="email"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {submitted ? (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                void submit();
              }}
              disabled={submitting || !email}
            >
              Send
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
